import { Chip, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { get, isEmpty, map } from 'lodash'
import { useEffect, useState } from 'react'
import { UserFilterOptions, UserHistoryParams } from '../../../api/models'
import CustomDialog from '../../../components/dialogs/CustomDialog/CustomDialog'
import { FormFieldLabel } from '../../../components/form/FormFieldLabel'
import styles from './FiltersDialog.module.css'

interface FiltersDialogProps {
  open: boolean
  primaryAction: (filters: object) => Promise<void>
  primaryActionButtonText: string
  secondaryAction: () => void
  secondaryActionButtonText: string
  title: string
  filters: UserHistoryParams
  filterOptions: UserFilterOptions | null
}

const FiltersDialog = ({
  open,
  primaryAction,
  primaryActionButtonText,
  secondaryAction,
  secondaryActionButtonText,
  title,
  filters,
  filterOptions,
}: FiltersDialogProps) => {
  const [internalFilters, setInternalFilters] = useState<UserHistoryParams>(filters)

  useEffect(() => {
    setInternalFilters(filters)
  }, [filters])

  return (
    <CustomDialog
      open={open}
      primaryAction={async () => await primaryAction(internalFilters)}
      primaryActionButtonText={primaryActionButtonText}
      secondaryAction={() => {
        secondaryAction()
        setInternalFilters(filters)
      }}
      secondaryActionButtonText={secondaryActionButtonText}
      title={title}
      clearFilters={() =>
        setInternalFilters(prev => ({
          ...prev,
          action_type: '',
          start_date: '',
          end_date: '',
          processed_by_user_id: '',
        }))
      }
      clearFiltersButtonText="Clear All"
      fullWidth>
      <div className={styles.filtersDialogContent}>
        <div>
          <FormFieldLabel text="Actions" />
          <div className={styles.chipOptions}>
            <div className={styles.chipOptionsList}>
              <Chip
                label="Show All"
                className={isEmpty(get(internalFilters, 'action_type')) ? styles.chipOptionSelected : styles.chipOption}
                onClick={() => setInternalFilters({ ...internalFilters, action_type: '' })}
              />
              <Chip
                key={'add_user'}
                label={'Add New User'}
                className={
                  get(internalFilters, 'action_type') === 'add_user' ? styles.chipOptionSelected : styles.chipOption
                }
                onClick={() => setInternalFilters({ ...internalFilters, action_type: 'add_user' })}
              />
              <Chip
                key={'change_role'}
                label={'Changed Role'}
                className={
                  get(internalFilters, 'action_type') === 'change_role' ? styles.chipOptionSelected : styles.chipOption
                }
                onClick={() => setInternalFilters({ ...internalFilters, action_type: 'change_role' })}
              />
              <Chip
                key={'delete_user'}
                label={'Deleted User'}
                className={
                  get(internalFilters, 'action_type') === 'delete_user' ? styles.chipOptionSelected : styles.chipOption
                }
                onClick={() => setInternalFilters({ ...internalFilters, action_type: 'delete_user' })}
              />
            </div>
          </div>
        </div>
        <div className={styles.selectOptionsContainer}>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text="Processed by" />
            <FormControl
              fullWidth
              size="small">
              {isEmpty(get(internalFilters, 'processed_by_user_id')) && <InputLabel shrink={false}>Select</InputLabel>}
              <Select
                size="small"
                value={get(internalFilters, 'processed_by_user_id') ?? ''}
                onChange={(e: SelectChangeEvent) => {
                  setInternalFilters({
                    ...internalFilters,
                    processed_by_user_id: e.target.value,
                  })
                }}>
                <MenuItem
                  key={'no-processed-by'}
                  value={undefined}>
                  All
                </MenuItem>
                {map(filterOptions?.users, option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <div className={styles.selectOptionsContainer}>
          <div className={styles.selectOptionsItem}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormFieldLabel text="Start date" />
              <FormControl
                size="small"
                fullWidth>
                <DatePicker
                  className={styles.dateFormField}
                  value={
                    isEmpty(get(internalFilters, 'start_date')) ? null : dayjs.utc(get(internalFilters, 'start_date'))
                  }
                  onChange={value => {
                    setInternalFilters({ ...internalFilters, start_date: value?.toISOString() ?? undefined })
                  }}
                  slotProps={{
                    day: { classes: { selected: styles.selectedDay } },
                    yearButton: { className: styles.yearButton },
                    textField: {
                      size: 'small',
                      inputProps: {
                        className: styles.dateInput,
                      },
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </div>
          <div className={styles.selectOptionsItem}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormFieldLabel text="End date" />
              <FormControl
                size="small"
                fullWidth>
                <DatePicker
                  className={styles.dateFormField}
                  value={isEmpty(get(internalFilters, 'end_date')) ? null : dayjs.utc(get(internalFilters, 'end_date'))}
                  onChange={value => {
                    setInternalFilters({ ...internalFilters, end_date: value?.toISOString() ?? undefined })
                  }}
                  slotProps={{
                    day: { classes: { selected: styles.selectedDay } },
                    yearButton: { className: styles.yearButton },
                    textField: {
                      size: 'small',
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </div>
        </div>
      </div>
    </CustomDialog>
  )
}

export default FiltersDialog
